import { useEffect, useMemo, useState } from "react";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { Link2, Copy, Check, Trash2, Lock, MessageSquare, Eye, ExternalLink, Calendar } from "lucide-react";

function timeLeft(iso) {
  if (!iso) return "Never expires";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "Expired";
  const d = Math.floor(ms / 86400000);
  if (d > 1) return `${d} days left`;
  const h = Math.floor(ms / 3600000);
  return `${h} hours left`;
}

function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return null;
  return ms / 86400000;
}

function ageBucket(days) {
  if (days === null || days === undefined) return null;
  if (days <= 3) return "fresh";
  if (days <= 7) return "aging";
  if (days <= 14) return "stale";
  return "cold";
}

const AGE_PILL = {
  fresh: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30",
  aging: "text-[var(--brand-accent)] border-amber-900/50 bg-amber-950/30",
  stale: "text-orange-400 border-orange-900/50 bg-orange-950/30",
  cold:  "text-red-400 border-red-900/50 bg-red-950/30",
};
const AGE_DOT = {
  fresh: "bg-emerald-500",
  aging: "bg-[var(--brand-accent)]",
  stale: "bg-orange-500",
  cold:  "bg-red-500",
};

function ageLabel(days) {
  if (days === null || days === undefined) return null;
  if (days < 1) return "today";
  return `${Math.round(days)}d`;
}

export default function Shares() {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [shares, setShares] = useState([]);
  const [view, setView] = useState("grid");
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data));
    api.get("/shares").then((r) => setShares(r.data));
  }, []);

  const copy = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const remove = async (id) => {
    if (!window.confirm("Revoke this share link? It will stop working immediately.")) return;
    await api.delete(`/shares/${id}`);
    const r = await api.get("/shares");
    setShares(r.data);
  };

  const reload = () => api.get("/folders").then((r) => setFolders(r.data));

  // Sort: review shares (oldest-first) at the top, watch-only shares (newest-first) below
  const sortedShares = useMemo(() => {
    const isReview = (s) => s.allow_comments || s.allow_approval;
    const review = shares.filter(isReview).sort((a, b) =>
      new Date(a.created_at || 0) - new Date(b.created_at || 0)
    );
    const watch = shares.filter((s) => !isReview(s)).sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
    return [...review, ...watch];
  }, [shares]);

  return (
    <div className="flex min-h-screen grain-overlay">
      <Sidebar folders={folders} onRefresh={reload} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar view={view} setView={setView} onSearch={() => {}} searchValue="" onUpload={() => {}} canUpload={false} />
        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Manage</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Share links</h1>
            <p className="text-sm text-zinc-500 mt-1">{shares.length} active {shares.length === 1 ? "link" : "links"} for client review</p>
          </div>

          {shares.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 rounded-full border border-zinc-800 mx-auto flex items-center justify-center mb-4">
                <Link2 className="w-7 h-7 text-zinc-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold tracking-tight text-zinc-300">No share links yet</h3>
              <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Open any folder or video and click <strong>Share</strong> to create a client review link.</p>
            </div>
          ) : (
            <div data-testid={TID.sharesList} className="space-y-3">
              {sortedShares.map((s) => {
                const isReview = s.allow_comments || s.allow_approval;
                const days = daysSince(s.created_at);
                const bucket = isReview ? ageBucket(days) : null;
                const ageStr = ageLabel(days);
                return (
                <div key={s.id} className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-5 hover:border-zinc-700 transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-900 px-1.5 py-0.5 rounded">{s.kind}</span>
                        {!isReview && (
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-900 px-1.5 py-0.5 rounded">watch-only</span>
                        )}
                        {s.has_password && <Lock className="w-3 h-3 text-[var(--brand-accent)]" />}
                        {s.title && <span className="text-[10px] uppercase tracking-wider text-zinc-500">{s.title}</span>}
                        {ageStr && (
                          <span
                            data-testid={`share-age-pill-${s.id}`}
                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border tabular-nums flex items-center gap-1.5 ${bucket ? AGE_PILL[bucket] : "text-zinc-500 border-zinc-800"}`}
                            title={`Sent ${new Date(s.created_at).toLocaleString()}`}
                          >
                            {bucket && <span className={`w-1.5 h-1.5 rounded-full ${AGE_DOT[bucket]}`} aria-hidden="true" />}
                            {ageStr}
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-zinc-100 truncate">{s.target_title}</h3>
                      <div className="text-xs text-zinc-500 mt-1">
                        Created by {s.created_by_name} · <Calendar className="w-2.5 h-2.5 inline mb-0.5" /> {timeLeft(s.expires_at)}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {s.view_count} views</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {s.comment_count} comments</span>
                      </div>
                      <div className="flex items-center gap-2 mt-3 bg-black/40 border border-zinc-800/80 rounded px-2 py-1.5">
                        <input
                          readOnly
                          value={s.url}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 bg-transparent text-[11px] text-zinc-300 focus:outline-none font-mono truncate"
                        />
                        <button onClick={() => copy(s.url, s.id)} className="text-xs flex items-center gap-1 text-[var(--brand-accent)] hover:text-[var(--brand-accent)]/80 shrink-0">
                          {copied === s.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {copied === s.id ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 items-end shrink-0">
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[var(--brand-accent)] p-1.5">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      {(user?.role === "admin" || s.created_by_id === user?.id) && (
                        <button onClick={() => remove(s.id)} data-testid={TID.deleteShareBtn(s.id)} className="text-zinc-500 hover:text-red-400 p-1.5" title="Revoke">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
